import { FC, useContext, useEffect, useRef, useState } from 'react';
import BookForm from './BookForm';
import BookSummary from './OrderSummary';
import { BookResponse } from 'ts/hotel/interfaces/BookResponse';
import BookPreview from './BookPreview';
import { saveScreenshot } from 'utils/utils';
import { useLocation, useSearchParams } from 'react-router-dom';
import { getClaudeInfo, updatePaymentBlock } from 'services/hotel';
import { getHotelIndex, getKey } from 'utils/hotel';
import usePageInMemory from 'hooks/usePageInMemory';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useGetPage } from 'hooks/useGetPage';
import { BookModel } from 'ts/hotel/classes/BookModel';
import { useTranslation } from 'react-i18next';

import Button from 'components/ui/Button/Button';
import { faArrowCircleLeft, faSearch } from '@fortawesome/free-solid-svg-icons';
import BookCancellation from './BookCancellation';
import AiNotes from './AiNotes';
import { MainContext } from 'contexts/MainContext';
import { HotelConfigContext } from 'contexts/HotelConfigContext';

const BookPage: FC = () => {
  const [bookResponse, setBookResponse] = useState<BookResponse>();
  const [book, setBook] = useState<BookResponse>();
  const [showPreview, setShowPreview] = useState(false);
  const bookRef = useRef<HTMLDivElement>(null);
  const [companyId, setCompanyId] = useState('');
  const [tripId, setTripId] = useState('0');
  const [saleValue, setSaleValue] = useState('');
  const location = useLocation();
  const [urlParams] = useSearchParams();
  const { hotelIndex } = useContext(HotelConfigContext);
  const [specialRequestMessage, setSpecialRequestMessage] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isApartmentAccepted, setIsApartmentAccepted] = useState(false);
  const { i18n, t } = useTranslation();
  const hotelIndexStr = getHotelIndex(hotelIndex);
  const backModule = 'hotel' + hotelIndexStr;
  const backHome = '/hotels' + hotelIndexStr;
  const [docket, setDocket] = useState('');
  const [tourSubject, setTourSubject] = useState('');
  const [aiNotesHidden, setAiNotesHidden] = useState(false);
  const { user } = useContext(MainContext);

  const [paxes, setPaxes] = useState<BookModel>();
  const idbKey = location.pathname + location.search;
  const { onClickBack, onClickSearch } = usePageInMemory(idbKey, () => { });
  const searchToken = sessionStorage.getItem(getKey('searchTokenHotels', hotelIndex));
  const [claudeRes, setClaudeRes] = useState<any>();
  useGetPage('Hotels/Book');

  useEffect(() => {

    (async () => {
      if (!user?.claudeAvailable) return;
      const parseClaudeRes = (claudeRes: string) => {
        try {
          let jsonString = claudeRes.trim();
          if (jsonString.startsWith('json\n')) {
            jsonString = jsonString.substring(5).trim();
          }

          const parsed = JSON.parse(jsonString);
          return parsed;
        } catch (error) {
          console.error('Error parsing Claude response:', error);
          return null;
        }
      };

      let attempts = 0;
      const maxAttempts = 15;
      const retryInterval = 4000;

      const tryGetClaudeInfo = async () => {
        attempts++;
        const claudeRes = await getClaudeInfo(searchToken || '');

        if (claudeRes && claudeRes.trim() !== '') {
          const jsonClaudeRes = parseClaudeRes(claudeRes);
          setClaudeRes(jsonClaudeRes);
          return;
        }

        if (attempts < maxAttempts) {
          setTimeout(tryGetClaudeInfo, retryInterval);
        } else {
          setAiNotesHidden(true);
        }
      };

      setTimeout(tryGetClaudeInfo, retryInterval);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newBookResponse = sessionStorage.getItem(getKey('detailsHotel', hotelIndex));
    if (newBookResponse) {
      const newBook = JSON.parse(newBookResponse);
      setBookResponse(newBook);
      setPaxes(new BookModel(newBook.paxes, newBook.roomClass.hotelRooms));
    }
  }, [urlParams.get('id'), urlParams.get('session'), urlParams.get('room')]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPressContinue = async (newBook: BookResponse) => {
    await saveScreenshot(bookRef.current, 'book1', `Hotel${getHotelIndex(hotelIndex)}`);
    setBook(newBook);
    setShowPreview(true);
  };

  const takeScreenshot = async () => {
    await saveScreenshot(bookRef.current, 'book2', `Hotel${getHotelIndex(hotelIndex)}`);
  };
  const recountTotalPrice = async () => {
    let sourcePrice = 0;
    if (bookResponse?.credit2000PaymentInfo) {
      sourcePrice = bookResponse?.credit2000PaymentInfo?.fullPriceInShekel || 0;
    } else if (bookResponse?.multiCredit2000PaymentInfo) {
      sourcePrice = bookResponse?.multiCredit2000PaymentInfo.reduce(
        (acc, p) => acc + p.fullPriceInShekel,
        0
      );
    }
    const res = await updatePaymentBlock({
      packageCurrency: bookResponse?.currency || '',
      sourcePrice,
      addPrice: parseInt(saleValue || '0'),
      searchToken: bookResponse?.orderId + '',
      paymentCount: bookResponse?.multiCredit2000PaymentInfo?.length || undefined,
      lang: i18n.language,
    });

    if (!book || !res) return;
    setBook({
      ...book,
      credit2000PaymentInfo: res.credit2000PaymentInfo,
      multiCredit2000PaymentInfo: res.multiCredit2000PaymentInfo,
    });

    const newBookResponse = {
      ...bookResponse,
      credit2000PaymentInfo: res.credit2000PaymentInfo,
      multiCredit2000PaymentInfo: res.multiCredit2000PaymentInfo,
    };

    sessionStorage.setItem(getKey('detailsHotel', hotelIndex), JSON.stringify(newBookResponse));
  };

  return (
    <div className="details-wrapper hotel-book-wrapper" ref={bookRef}>
      <div className="flex-column w100p">
        {!showPreview && (
          <div className="mb-20 default-shadow flex-between dyn-change-header">
            <Button
              title={t('back')}
              icon={faArrowCircleLeft}
              onClick={() => onClickBack(backModule, 'details')}
              className="btn btn-link"
            />
            <Button
              title={t('search.new')}
              icon={faSearch}
              onClick={() => onClickSearch(backModule, backHome)}
              className="btn btn-link"
            />
          </div>
        )}
        {!!bookResponse && !showPreview && paxes && (
          <div>
            <div className="flex-between hotel-book mb-20">
              <BookCancellation
                book={book || bookResponse}
                docket={docket}
                setDocket={setDocket}
                tourSubject={tourSubject}
                setTourSubject={setTourSubject}
                isSubmitted={isSubmitted}
              />
              <BookSummary
                book={bookResponse}
                paxes={paxes}
                setSpecialRequestMessage={setSpecialRequestMessage}
                isSubmitted={isSubmitted}
                setIsApartmentAccepted={setIsApartmentAccepted}
                isApartmentAccepted={isApartmentAccepted}
              />
            </div>

            <AiNotes claudeRes={claudeRes} hidden={aiNotesHidden} />

            <BookForm
              book={book || bookResponse}
              onPressContinue={onPressContinue}
              onChangeCompanyId={setCompanyId}
              onChangeTripId={setTripId}
              tripId={tripId}
              companyId={companyId}
              form={paxes}
              setForm={setPaxes as any}
              isSubmitted={isSubmitted}
              setIsSubmitted={setIsSubmitted}
              isApartmentAccepted={isApartmentAccepted}
              docket={docket}
              tourSubject={tourSubject}
            />
          </div>
        )}
        {!!book && showPreview && (
          <BookPreview
            book={book}
            aiNotesHidden={aiNotesHidden}
            onPressBack={() => setShowPreview(false)}
            takeScreenshot={() => takeScreenshot()}
            companyId={companyId}
            tripId={tripId}
            saleValue={saleValue}
            specialRequestMessage={specialRequestMessage}
            setSaleValue={setSaleValue}
            recountTotalPrice={recountTotalPrice}
            claudeRes={claudeRes}

          />
        )}
      </div>
    </div>
  );
};

export default withErrorBoundary(BookPage, 'BookPage');
